import ELK from 'elkjs/lib/elk.bundled.js';

const elk = new ELK();

const NODE_WIDTH = 270;
const NODE_HEIGHT = 160;

const LAYOUT_OPTIONS = {
  'elk.algorithm': 'layered',
  'elk.direction': 'DOWN',
  'elk.layered.spacing.nodeNodeBetweenLayers': '140',
  'elk.spacing.nodeNode': '100',
  'elk.spacing.edgeNode': '40',
  'elk.spacing.edgeEdge': '30',
  'elk.portConstraints': 'FIXED_ORDER',
  'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
  'elk.layered.nodePlacement.favorStraightEdges': 'true',
  'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
  'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
  'elk.layered.layering.strategy': 'NETWORK_SIMPLEX',
  'elk.layered.thoroughness': '10',
  'elk.separateConnectedComponents': 'false',
};

export const runElkAutoLayout = async (nodes = [], edges = []) => {
  if (!Array.isArray(nodes) || nodes.length === 0) return nodes;

  const nodeTypeMap = new Map(nodes.map((n) => [n.id, n.type]));

  // Build ELK graph nodes & ports
  const elkNodes = nodes.map((node) => {
    const ports = [];

    // Input Port (NORTH) for all non-start nodes
    if (node.type !== 'start') {
      ports.push({
        id: `${node.id}-in`,
        layoutOptions: {
          'elk.port.side': 'NORTH',
        },
      });
    }

    // Output Ports (SOUTH)
    if (node.type === 'condition') {
      const cases = node.data?.cases || [];
      cases.forEach((c, idx) => {
        if (c.case_id) {
          ports.push({
            id: c.case_id,
            layoutOptions: {
              'elk.port.side': 'SOUTH',
              'elk.port.index': String(idx),
            },
          });
        }
      });
      // Mandatory False / Else port
      ports.push({
        id: 'false',
        layoutOptions: {
          'elk.port.side': 'SOUTH',
          'elk.port.index': String(cases.length),
        },
      });
    } else if (node.type === 'probability') {
      const branches = node.data?.branches || [];
      branches.forEach((b, idx) => {
        if (b.branch_id) {
          ports.push({
            id: b.branch_id,
            layoutOptions: {
              'elk.port.side': 'SOUTH',
              'elk.port.index': String(idx),
            },
          });
        }
      });
    } else if (node.type === 'start' || node.type === 'route') {
      ports.push({
        id: `${node.id}-out`,
        layoutOptions: {
          'elk.port.side': 'SOUTH',
          'elk.port.index': '0',
        },
      });
    }

    return {
      id: node.id,
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
      ports,
      layoutOptions: {
        'elk.portConstraints': 'FIXED_ORDER',
      },
    };
  });

  // Build ELK graph edges with guaranteed matching port IDs
  const elkEdges = edges.map((edge, i) => {
    const srcType = nodeTypeMap.get(edge.source);

    let sourcePort;
    if (srcType === 'start' || srcType === 'route') {
      sourcePort = `${edge.source}-out`;
    } else {
      sourcePort = edge.sourceHandle || 'false';
    }

    const targetPort = `${edge.target}-in`;

    return {
      id: edge.id || `elk-edge-${i}`,
      sources: [edge.source],
      targets: [edge.target],
      sourcePort,
      targetPort,
    };
  });

  const graph = {
    id: 'root-flow-graph',
    layoutOptions: LAYOUT_OPTIONS,
    children: elkNodes,
    edges: elkEdges,
  };

  try {
    const layoutedGraph = await elk.layout(graph);

    // Map calculated positions back to ReactFlow nodes
    const positionMap = new Map();
    (layoutedGraph.children || []).forEach((child) => {
      positionMap.set(child.id, {
        x: Math.round(child.x || 0),
        y: Math.round(child.y || 0),
      });
    });

    return nodes.map((node) => {
      const newPos = positionMap.get(node.id);
      return newPos
        ? { ...node, position: newPos }
        : node;
    });
  } catch (err) {
    console.error('ELK auto-layout error:', err);
    return nodes;
  }
};
